import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Box,
  Divider,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentForm from '../PaymentForm';
import {
  isMinorBirthDate,
  computeVariantAvailableSeats,
} from '../../utils/guestEventEligibility';
import { formatVariantSchedule, getVariantPrice } from '../../utils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC, {
  locale: 'it',
});

function GuestEventBookingDrawer({
  isOpen,
  placement,
  onClose,
  event,
  booking,
}) {
  const {
    step,
    setStep,
    requiresVariant,
    guestVariants = [],
    selectedVariant,
    setSelectedVariantId,
    quantity,
    setQuantity,
    maxQty,
    customer,
    setCustomer,
    participants,
    updateParticipant,
    updateParticipantGenitore,
    consents,
    setConsents,
    paymentError,
    paymentLoading,
    clientSecret,
    showPaymentForm,
    totalAmount,
    unitPrice,
    onlinePaymentOnly,
    socioEmailWarning,
    socioEmailDismissed,
    setSocioEmailDismissed,
    existingBookingWarning,
    checkCustomerEmail,
    handleConfirmCash,
    handleConfirmFree,
    handleProceedToStripe,
    onPaymentSuccess,
    isFreeEvent,
  } = booking;

  const firstStep = requiresVariant ? 'Variante e posti' : 'Quantità';
  const steps = isFreeEvent
    ? [firstStep, 'Acquirente', 'Partecipanti', 'Consensi', 'Conferma']
    : [firstStep, 'Acquirente', 'Partecipanti', 'Consensi', 'Pagamento'];
  const isLastStep = step >= steps.length - 1;

  const goNext = async () => {
    if (step === 0) {
      const err = booking.validateVariant?.();
      if (err) {
        booking.setPaymentError(err);
        return;
      }
    }
    if (step === 1) {
      let emailCheck = { hasExistingBooking: false, isRegisteredSocio: false };
      if (customer.email?.includes('@')) {
        emailCheck = await checkCustomerEmail(customer.email);
      }
      const err = booking.validateCustomer?.();
      if (err) {
        booking.setPaymentError(err);
        return;
      }
      if (emailCheck.hasExistingBooking) {
        booking.setPaymentError('');
        return;
      }
      if (emailCheck.isRegisteredSocio && !socioEmailDismissed) {
        booking.setPaymentError(
          'Conferma di voler continuare come ospite oppure accedi come socio'
        );
        return;
      }
    }
    if (step === 2) {
      const err = booking.validateParticipants?.();
      if (err) {
        booking.setPaymentError(err);
        return;
      }
    }
    if (step === 3) {
      const err = booking.validateConsents?.();
      if (err) {
        booking.setPaymentError(err);
        return;
      }
    }
    booking.setPaymentError('');
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goBack = () => {
    booking.setPaymentError('');
    setStep((s) => Math.max(s - 1, 0));
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement={placement || 'right'}
      onClose={onClose}
      size="md"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          Partecipa come ospite — {event?.Nome}
          {selectedVariant && step > 0 ? ` · ${selectedVariant.Nome}` : ''}
        </DrawerHeader>
        <DrawerBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Passo {step + 1} di {steps.length}: {steps[step]}
          </Text>

          {paymentError && !existingBookingWarning ? (
            <Alert status="error" mb={4} borderRadius="md">
              <AlertIcon />
              <AlertDescription>{paymentError}</AlertDescription>
            </Alert>
          ) : null}

          {step === 0 && (
            <VStack align="stretch" spacing={4}>
              {requiresVariant ? (
                <FormControl isRequired>
                  <FormLabel>A cosa vuoi partecipare?</FormLabel>
                  {guestVariants.length ? (
                    <RadioGroup
                      value={selectedVariant?.documentId || ''}
                      onChange={(id) => {
                        setSelectedVariantId(id);
                        booking.setPaymentError('');
                      }}
                    >
                      <VStack align="stretch" spacing={2}>
                        {guestVariants.map((v) => {
                          const available = computeVariantAvailableSeats(v);
                          const soldOut = available <= 0;
                          const price = getVariantPrice(v, event);
                          const { date, time } = formatVariantSchedule(v);
                          return (
                            <Box
                              key={v.documentId}
                              borderWidth="1px"
                              borderRadius="md"
                              p={3}
                              opacity={soldOut ? 0.6 : 1}
                            >
                              <Radio
                                value={v.documentId}
                                isDisabled={soldOut}
                                data-testid={`guest-variant-${v.documentId}`}
                              >
                                <Text fontWeight="semibold">
                                  {v.Nome || 'Variante'}
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                  {[date, time].filter(Boolean).join(' · ')}
                                </Text>
                                <Text fontSize="sm">
                                  {price === 0
                                    ? 'Gratis'
                                    : `€${price.toFixed(2)} a persona`}
                                  {soldOut
                                    ? ' — Posti esauriti'
                                    : available < 999
                                      ? ` — ${available} posti liberi`
                                      : ''}
                                </Text>
                              </Radio>
                            </Box>
                          );
                        })}
                      </VStack>
                    </RadioGroup>
                  ) : (
                    <Text color="gray.600">
                      Nessuna variante è prenotabile come ospite.
                    </Text>
                  )}
                </FormControl>
              ) : null}
              {!requiresVariant || selectedVariant ? (
                <Text>
                  {isFreeEvent
                    ? requiresVariant
                      ? 'Partecipazione gratuita.'
                      : 'Evento gratuito.'
                    : `Prezzo: €${unitPrice.toFixed(2)} a persona. Totale stimato: €${totalAmount.toFixed(2)}`}
                </Text>
              ) : null}
              <FormControl>
                <FormLabel>Numero di partecipanti</FormLabel>
                <NumberInput
                  min={1}
                  max={maxQty}
                  value={quantity}
                  onChange={(_, v) => setQuantity(v || 1)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </VStack>
          )}

          {step === 1 && (
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired>
                <FormLabel>Nome (chi prenota)</FormLabel>
                <Input
                  value={customer.firstName}
                  onChange={(e) =>
                    setCustomer((c) => ({ ...c, firstName: e.target.value }))
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Cognome</FormLabel>
                <Input
                  value={customer.lastName}
                  onChange={(e) =>
                    setCustomer((c) => ({ ...c, lastName: e.target.value }))
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={customer.email}
                  onChange={(e) =>
                    setCustomer((c) => ({ ...c, email: e.target.value }))
                  }
                  onBlur={(e) => checkCustomerEmail(e.target.value)}
                />
              </FormControl>
              {existingBookingWarning ? (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    Esiste già una prenotazione attiva con questa email per
                    {selectedVariant ? ' questa variante' : ' questo evento'}.
                    Per modifiche o cancellazioni contatta la segreteria CIL a{' '}
                    <Text
                      as="a"
                      href="mailto:info@cilassociazione.it"
                      color="blue.600"
                      textDecoration="underline"
                    >
                      info@cilassociazione.it
                    </Text>{' '}
                    oppure{' '}
                    <Text
                      as={RouterLink}
                      to="/contattaci"
                      color="blue.600"
                      textDecoration="underline"
                    >
                      usa il modulo di contatto
                    </Text>
                    .
                  </AlertDescription>
                </Alert>
              ) : null}
              {socioEmailWarning &&
              !socioEmailDismissed &&
              !existingBookingWarning ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertDescription mb={2}>
                      Questa email è associata a un socio CIL. Puoi accedere per
                      prenotare con la tessera.
                    </AlertDescription>
                    <HStack>
                      <Button
                        as={RouterLink}
                        to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
                        size="sm"
                        colorScheme="blue"
                      >
                        Accedi
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSocioEmailDismissed(true);
                          booking.setPaymentError('');
                        }}
                      >
                        Continua come ospite
                      </Button>
                    </HStack>
                  </Box>
                </Alert>
              ) : null}
              <FormControl isRequired>
                <FormLabel>Telefono</FormLabel>
                <Input
                  value={customer.phone}
                  onChange={(e) =>
                    setCustomer((c) => ({ ...c, phone: e.target.value }))
                  }
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Data di nascita (acquirente)</FormLabel>
                <Input
                  type="date"
                  value={customer.birthDate}
                  onChange={(e) =>
                    setCustomer((c) => ({ ...c, birthDate: e.target.value }))
                  }
                />
              </FormControl>
            </VStack>
          )}

          {step === 2 && (
            <VStack align="stretch" spacing={6}>
              {participants.map((p, index) => (
                <Box key={p.slotId} p={3} borderWidth="1px" borderRadius="md">
                  <Text fontWeight="bold" mb={2}>
                    Partecipante {index + 1}
                  </Text>
                  <VStack align="stretch" spacing={2}>
                    <FormControl isRequired>
                      <FormLabel>Nome</FormLabel>
                      <Input
                        value={p.firstName}
                        onChange={(e) =>
                          updateParticipant(index, 'firstName', e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Cognome</FormLabel>
                      <Input
                        value={p.lastName}
                        onChange={(e) =>
                          updateParticipant(index, 'lastName', e.target.value)
                        }
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Data di nascita</FormLabel>
                      <Input
                        type="date"
                        value={p.birthDate}
                        onChange={(e) =>
                          updateParticipant(index, 'birthDate', e.target.value)
                        }
                      />
                    </FormControl>
                    {isMinorBirthDate(p.birthDate) ? (
                      <>
                        <Divider />
                        <Text fontSize="sm" fontWeight="semibold">
                          Genitore / tutore
                        </Text>
                        <FormControl isRequired>
                          <FormLabel>Nome genitore</FormLabel>
                          <Input
                            value={p.genitore?.nome || ''}
                            onChange={(e) =>
                              updateParticipantGenitore(
                                index,
                                'nome',
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel>Cognome genitore</FormLabel>
                          <Input
                            value={p.genitore?.cognome || ''}
                            onChange={(e) =>
                              updateParticipantGenitore(
                                index,
                                'cognome',
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                        <FormControl isRequired>
                          <FormLabel>Codice fiscale genitore</FormLabel>
                          <Input
                            value={p.genitore?.codiceFiscale || ''}
                            onChange={(e) =>
                              updateParticipantGenitore(
                                index,
                                'codiceFiscale',
                                e.target.value
                              )
                            }
                          />
                        </FormControl>
                      </>
                    ) : null}
                  </VStack>
                </Box>
              ))}
            </VStack>
          )}

          {step === 3 && (
            <VStack align="stretch" spacing={3}>
              <Checkbox
                isChecked={consents.terminiCondizioni}
                onChange={(e) =>
                  setConsents((c) => ({
                    ...c,
                    terminiCondizioni: e.target.checked,
                  }))
                }
              >
                Accetto i termini e condizioni
              </Checkbox>
              <Checkbox
                isChecked={consents.autorizzazioneFotografie}
                onChange={(e) =>
                  setConsents((c) => ({
                    ...c,
                    autorizzazioneFotografie: e.target.checked,
                  }))
                }
              >
                Autorizzo l&apos;uso di fotografie e video (facoltativo)
              </Checkbox>
              <Checkbox
                isChecked={consents.consensoPubblicazione}
                onChange={(e) =>
                  setConsents((c) => ({
                    ...c,
                    consensoPubblicazione: e.target.checked,
                  }))
                }
              >
                Acconsento alla pubblicazione di fotografie e contenuti
                multimediali (immagini/video) su web e social (facoltativo)
              </Checkbox>
            </VStack>
          )}

          {step === 4 && !showPaymentForm && isFreeEvent ? (
            <VStack align="stretch" spacing={4}>
              <Text>
                Confermi l&apos;iscrizione gratuita per {quantity}{' '}
                {quantity === 1 ? 'partecipante' : 'partecipanti'}?
              </Text>
              <Button
                colorScheme="blue"
                onClick={handleConfirmFree}
                isLoading={paymentLoading}
              >
                Conferma iscrizione
              </Button>
            </VStack>
          ) : null}

          {step === 4 && !showPaymentForm && !isFreeEvent ? (
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="bold">Totale: €{totalAmount.toFixed(2)}</Text>
              {!onlinePaymentOnly ? (
                <Button
                  colorScheme="yellow"
                  onClick={handleConfirmCash}
                  isLoading={paymentLoading}
                >
                  Prenota e paga in contanti in sede
                </Button>
              ) : null}
              <Button
                colorScheme="blue"
                onClick={handleProceedToStripe}
                isLoading={paymentLoading}
              >
                Paga con carta
              </Button>
            </VStack>
          ) : null}

          {step === 4 && showPaymentForm && clientSecret ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm onSuccess={onPaymentSuccess} />
            </Elements>
          ) : null}
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px">
          <HStack w="100%" justify="space-between">
            <Button variant="outline" onClick={goBack} isDisabled={step === 0}>
              Indietro
            </Button>
            {!isLastStep ? (
              <Button colorScheme="blue" onClick={goNext}>
                Avanti
              </Button>
            ) : (
              <Button variant="ghost" onClick={onClose}>
                Chiudi
              </Button>
            )}
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default GuestEventBookingDrawer;
